import { useState } from 'react';
import axiosPost from '../../../helpers/axios-helper';
import './ReorderHint.css';

/**
 * Drag-to-reorder for a module's main list, which sets the order its
 * carousel uses on the country page.
 *
 * Ordering is per country, so this only turns on once a country filter is
 * chosen: with "All countries" showing, a dragged row has no single list to
 * be positioned within.
 *
 * Usage:
 *   const reorder = useCarouselReorder({
 *     module: 'hotels', items: hotels, setItems: setHotels,
 *     countryId: filters.country_id,
 *   });
 *   ...
 *   <tr {...reorder.rowProps(index)}>
 */
export default function useCarouselReorder({ module, items, setItems, countryId, onSaved }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Choosing a country in the dropdown does not refetch on its own — the
  // list only narrows once "Apply Filters" is pressed. So the loaded rows
  // are what decides whether dragging makes sense, not the dropdown: if the
  // list still holds more than one country, positions dragged within it
  // would not line up with that country's carousel.
  const loadedCountries = new Set(
    (items || []).map((it) => String(it.country_id)).filter((v) => v && v !== 'undefined')
  );
  const listMatchesFilter =
    loadedCountries.size === 1 && loadedCountries.has(String(countryId));

  const enabled = !!countryId && listMatchesFilter;

  const persist = (ordered) => {
    setIsSaving(true);
    setError('');
    axiosPost(
      'content/reorder',
      { 'Content-Type': 'application/json' },
      {
        module,
        country_id: parseInt(countryId),
        items: ordered.map((it) => it.id)
      },
      (resp) => {
        setIsSaving(false);
        if (resp?.success) {
          if (onSaved) onSaved();
        } else {
          setError(resp?.response || 'Could not save the new order.');
        }
      }
    );
  };

  const onDragStart = (e, index) => {
    if (!enabled) return;
    setDragIndex(index);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
    } catch (err) {}
  };

  const onDragOver = (e) => {
    if (!enabled) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch (err) {}
  };

  const onDrop = (e, index) => {
    if (!enabled) return;
    e.preventDefault();
    const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
    setDragIndex(null);
    if (isNaN(from) || from === index) return;

    const next = Array.from(items);
    const [moved] = next.splice(from, 1);
    next.splice(index, 0, moved);

    // Show the new order straight away; the request only persists it.
    setItems(next);
    persist(next);
  };

  const onDragEnd = () => setDragIndex(null);

  const rowProps = (index) => ({
    draggable: enabled,
    onDragStart: (e) => onDragStart(e, index),
    onDragOver,
    onDrop: (e) => onDrop(e, index),
    onDragEnd,
    style: {
      cursor: enabled ? 'move' : undefined,
      opacity: dragIndex === index ? 0.5 : 1
    }
  });

  return { enabled, isSaving, error, dragIndex, rowProps };
}
